import { useOthers, useSelf, useUpdateMyPresence } from "@configs/liveblocks.config"
import { atom, useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect, useRef } from "react"

import { initCrossTabSession } from "@utils/initCrossTabSession"

import { COLLABORATION_USER_COLORS } from "@constants/storage"

const COLLABORATION_PALETTE = [
    "var(--live-collaboration-crimson)",
    "var(--live-collaboration-pink)",
    "var(--live-collaboration-plum)",
    "var(--live-collaboration-violet)",
    "var(--live-collaboration-indigo)",
    "var(--live-collaboration-blue)",
    "var(--live-collaboration-cyan)",
    "var(--live-collaboration-teal)",
    "var(--live-collaboration-orange)",
    "var(--live-collaboration-amber)",
]

type UserColor = {
    userId: string
    color: string
}

export const userColorsAtom = atomWithStorage<UserColor[]>(COLLABORATION_USER_COLORS, [])

const myColorAtom = atom<string | null>(null)

function pickColor(userId: string, assignments: UserColor[]): string {
    const assigned = assignments.find((assignment) => assignment.userId === userId)
    if (assigned) {
        return assigned.color
    } else {
        const usedColors = assignments.map((assignment) => assignment.color)
        const available = COLLABORATION_PALETTE.filter((color) => !usedColors.includes(color))
        const pool = available.length > 0 ? available : COLLABORATION_PALETTE
        return pool[Math.floor(Math.random() * pool.length)]
    }
}

/**
 * Read collaboration color assignments without subscribing to Liveblocks.
 *
 * Split from the writer so avatar surfaces can look up colors without opening a
 * presence subscription: every mounted avatar calls this, but only a live room
 * mounts `useCollaborationColorSync`. Subscribing here would couple every
 * avatar's re-render to unrelated presence broadcasts and write presence back
 * from surfaces that should only read it.
 */
export function useCollaborationColors() {
    const [assignments] = useAtom(userColorsAtom)
    const [myColor] = useAtom(myColorAtom)

    const getUserColor = (userId: string): string | null => {
        const found = assignments.find((assignment) => assignment.userId === userId)
        return found ? found.color : null
    }

    return {
        myColor,
        getUserColor,
        allAssignments: assignments,
    }
}

/**
 * Own the collaboration presence writes for a single live room.
 *
 * Holds the only Liveblocks subscription and reconciles the shared color atoms,
 * so readers stay subscription-free. Reconciliation is strictly additive within
 * a browser session: writers bound to different rooms (a page room and a nested
 * lock room, or another tab) see different member sets, so any writer that
 * *removed* absent users would fight a writer that still sees them, oscillating
 * the shared atom forever. Only adding keeps every writer convergent — the
 * per-session reset in `initCrossTabSession` is what reclaims colors. The
 * broadcast guards below stop the presence ping-pong: an unrelated `others`
 * change no longer rebroadcasts a color that has not moved.
 */
export function useCollaborationColorSync() {
    const self = useSelf()
    const others = useOthers()
    const updatePresence = useUpdateMyPresence()

    const [assignments, setAssignments] = useAtom(userColorsAtom)
    const [myColor, setMyColor] = useAtom(myColorAtom)

    const lastBroadcastColor = useRef<string | null>(null)

    useEffect(() => {
        const cleanup = initCrossTabSession({
            onNewSession: () => setAssignments([]),
        })

        return cleanup
    }, [])

    useEffect(() => {
        if (!self?.id) {
            return
        }

        const updatedAssignments = [...assignments]

        others.forEach(({ id, presence }) => {
            const userId = String(id)
            const alreadyAssigned = updatedAssignments.some((assignment) => assignment.userId === userId)
            if (presence?.color && !alreadyAssigned) {
                updatedAssignments.push({ userId, color: presence.color })
            }
        })

        const nextMyColor = pickColor(self.id, updatedAssignments)
        if (nextMyColor !== myColor) {
            setMyColor(nextMyColor)
        }
        if (nextMyColor !== lastBroadcastColor.current) {
            updatePresence({ color: nextMyColor })
            lastBroadcastColor.current = nextMyColor
        }

        const myAssignmentExists = updatedAssignments.some((assignment) => assignment.userId === self.id)
        if (!myAssignmentExists) {
            updatedAssignments.push({ userId: self.id, color: nextMyColor })
        }

        if (updatedAssignments.length !== assignments.length) {
            setAssignments(updatedAssignments)
        }
    }, [self?.id, others, assignments, setAssignments, updatePresence, setMyColor, myColor])
}
